import { Button } from "primereact/button";
import { useEffect, useState } from "react";
import { useAppStore } from "../../../../../store/app.store";
import useGetTableForm from "../../../../hooks/useGetTableForm";
import Icon from "../../../utils/Icons/Icon";
import OurDropdown from "../../../utils/OurDropdown/OurDropdown";
import Spinner from "../../../utils/Spinner/Spinner";
import "./index.scss";

export interface ImportDataStepTwoProps {
  navigateToStep(step: number): void;
}

export default function ImportDataStepTwo({ navigateToStep }: ImportDataStepTwoProps) {
  const { importTableDataInputState, setImportTableDataInputState } = useAppStore();
  const { tableForm, loadingTableForm, fetchTableForm } = useGetTableForm();
  const [mappings, setMappings] = useState<{ id: string; source?: string; target?: string }[]>([]);
  const errors = new Map<string, string>();
  const loading = false;
  const sourceFields =
    (importTableDataInputState?.fromFile || importTableDataInputState?.fromAPI)?.parsedData
      ?.objectKeys || [];

  const [loadingMappings, setLoadingMappings] = useState(false);

  const addMapping = () => {
    setMappings([
      ...mappings,
      { id: `mapping-${Date.now()}-${Math.random()}`, source: undefined, target: undefined },
    ]);
  };

  const updateMappings = (index: number, key: "source" | "target", value: string) => {
    setLoadingMappings(true);
    setTimeout(() => {
      setLoadingMappings(false);
    }, 200);
    const newMappings = [...mappings];
    newMappings[index][key] = value;
    setMappings(newMappings);
  };

  useEffect(() => {
    const columnsMapping = importTableDataInputState?.fromAPI?.columnsMapping
      ? importTableDataInputState.fromAPI.columnsMapping
      : importTableDataInputState?.fromFile?.columnsMapping
        ? importTableDataInputState.fromFile.columnsMapping
        : undefined;
    if (!columnsMapping || Object.keys(columnsMapping).length === 0) {
      setMappings([
        {
          id: `mapping-${Date.now()}-${Math.random()}`,
          source: undefined,
          target: undefined,
        },
      ]);
    } else {
      setMappings(
        Object.entries(columnsMapping).map(([target, source], index) => ({
          id: `mapping-${Date.now()}-${index}`,
          source,
          target,
        })),
      );
    }
  }, []);
  useEffect(() => {
    if (!importTableDataInputState) return;
    fetchTableForm({
      referenceId: importTableDataInputState.referenceId,
      tableId: importTableDataInputState.tableId,
    });
  }, [importTableDataInputState]);
  useEffect(() => {
    if (mappings.length === 0 || mappings.some((mapping) => !mapping.source || !mapping.target)) {
      return;
    }
    const oldState = importTableDataInputState;
    if (!oldState) return;
    const newState = {
      ...oldState,
      fromAPI: oldState.fromAPI
        ? {
            ...oldState.fromAPI,
            columnsMapping: mappings.reduce(
              (acc, mapping) => {
                if (mapping.source && mapping.target) {
                  acc[mapping.target] = mapping.source;
                }
                return acc;
              },
              {} as Record<string, string>,
            ),
          }
        : undefined,
      fromFile: oldState.fromFile
        ? {
            ...oldState.fromFile,
            columnsMapping: mappings.reduce(
              (acc, mapping) => {
                if (mapping.source && mapping.target) {
                  acc[mapping.target] = mapping.source;
                }
                return acc;
              },
              {} as Record<string, string>,
            ),
          }
        : undefined,
    };
    setImportTableDataInputState(newState);
  }, [mappings]);

  const getAvailableSourceFields = (currentMapping: { source?: string }, currentIndex: number) => {
    return sourceFields.filter(
      (field) =>
        field === currentMapping.source ||
        !mappings.some((m, i) => i !== currentIndex && m.source === field),
    );
  };

  const getAvailableTargetFields = (currentMapping: { target?: string }, currentIndex: number) => {
    return tableForm.filter(
      (table) =>
        table.name === currentMapping.target ||
        !mappings.some((m, i) => i !== currentIndex && m.target === table.name),
    );
  };

  const hasAvailableSourceFields = sourceFields.some(
    (field) => !mappings.some((m) => m.source === field),
  );
  const hasAvailableTargetFields = tableForm.some(
    (table) => !mappings.some((m) => m.target === table.name),
  );
  const canAddMapping = hasAvailableSourceFields && hasAvailableTargetFields;

  return (
    <div className="ImportDataStepTwo">
      <div className="ImportDataStepTwo-card">
        <div className="ImportDataStepTwo-header">
          <h2 className="ImportDataStepOne-title">Configuration du mappage</h2>
          <Button
            className="Btn success sm icon"
            label="Ajouter un mappage"
            icon={<Icon name="plus" />}
            onClick={addMapping}
            disabled={errors.size > 0 || loading || !canAddMapping}
          />
        </div>
        {loadingMappings && <Spinner />}
        {!loadingMappings &&
          mappings.length > 0 &&
          mappings.map((mapping, index) => {
            const availableSourceFields = getAvailableSourceFields(mapping, index);
            const availableTargetFields = getAvailableTargetFields(mapping, index);

            return (
              <div key={mapping.id} className="ImportDataStepTwo-mappingForm">
                <label className="Field w-full">
                  <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                    Champ source
                  </span>
                  <OurDropdown<string>
                    initialOptions={availableSourceFields.map((field) => ({
                      value: field,
                      label: field,
                    }))}
                    className="sm RelationCard-dropdown"
                    selectedOption={mapping.source}
                    onChange={(val) => {
                      updateMappings(index, "source", val);
                    }}
                    placeholder="Sélectionner"
                  />
                </label>

                <label className="Field w-full">
                  <span className="Field-label" style={{ fontWeight: 600, fontSize: "15px" }}>
                    Champ cible
                  </span>
                  {loadingTableForm ? (
                    <Spinner />
                  ) : (
                    <OurDropdown<string>
                      initialOptions={availableTargetFields.map((table) => ({
                        value: table.name,
                        label: table.label,
                      }))}
                      className="sm RelationCard-dropdown"
                      selectedOption={mapping.target}
                      onChange={(val) => updateMappings(index, "target", val)}
                      placeholder="Sélectionner"
                    />
                  )}
                </label>

                <div className="ImportDataStepTwo-actions">
                  <p className="ImportDataStepTwo-actionsTitle">Actions</p>
                  <button
                    type="button"
                    className="ImportDataStepTwo-removeMappingBtn"
                    onClick={() => {
                      const newMappings = [...mappings];
                      newMappings.splice(index, 1);
                      setMappings(newMappings);
                    }}
                  >
                    <Icon name="trash" />
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      <div className="ImportDataStepTwo-footer">
        <Button
          className="Btn secondary outlined"
          label="Retour"
          onClick={() => navigateToStep(1)}
        />
        <Button
          className="Btn primary"
          label="Continuer"
          onClick={() => navigateToStep(3)}
          // disabled={firstTable && (firstTable.name === "" || firstTable.columns.length === 0)}
        />
      </div>
    </div>
  );
}
